import React, { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "../../hooks/useToast";
import { useReadMessages } from "../../hooks/useReadMessages";
import LeadMarketplace from "./LeadMarketplace";
import SubscriptionPage from "./SubscriptionPage";
import SubscriptionBanner from "./SubscriptionBanner";
import TodayScreen from "./TodayScreen";
import TradieOverview from "./TradieOverview";
import BookJobModal from "./BookJobModal";
import LeadDetailPanel from "./LeadDetailPanel";
import Card from "../common/Card";
import EmptyState from "../common/EmptyState";
import Pipeline from "./Pipeline/Pipeline";
import { shouldShowMiniCRM } from "../../featureFlags";
import { isLeadUnlocked } from "../../utils/leadAccess";
import {
  getPipelineViewStorageKey,
  getOverviewDismissedStorageKey,
  normalizePipelineViewMode,
  shouldShowSetupOverview,
} from "../../utils/tradieWorkspace";
import { useGodMode } from "../../hooks/useGodMode";

function BusinessHeader({ profile }) {
  if (!profile) return null;
  const name = profile.business_name || profile.full_name;
  const logo = profile.logo_url;
  if (!name && !logo) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        marginBottom: "16px",
      }}
    >
      {logo && (
        <img
          src={logo}
          alt={name || "Business logo"}
          style={{
            width: "44px",
            height: "44px",
            borderRadius: "50%",
            objectFit: "cover",
            border: "2px solid rgba(255, 138, 0,0.35)",
            flexShrink: 0,
          }}
        />
      )}
      {name && (
        <span style={{ fontWeight: 700, fontSize: "1.05rem" }}>{name}</span>
      )}
    </div>
  );
}
export default function TradieDashboard({
  profile,
  requests,
  leadsLoading,
  updateRequestStatus,
  createOnsiteQuoteLead,
  saveQuote,
  unlockLead,
  unlockedLeadIds,
  creditBalance,
  getCustomerQuoteLink,
  getCustomerInvoiceLink,
  loadLeadDetails,
  businessId,
  bookJob,
  dismissLead,
  undismissLead,
  dismissedLeadIds,
  updateLeadStage,
  archiveLead,
  unarchiveLead,
  updateLeadContactDetails,
  subscription,
  showPricing: showPricingProp,
  setShowPricing: setShowPricingProp,
  onGoToSettings,
}) {
  const [selectedId, setSelectedId] = useState(requests[0]?.id || null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);

  function handleSelectLead(id) {
    setSelectedId(id);
    setMobileDetailOpen(true);
  }

  const [bookJobLead, setBookJobLead] = useState(null);

  const [localShowPricing, setLocalShowPricing] = useState(false);
  const showPricing = showPricingProp !== undefined ? showPricingProp : localShowPricing;
  const setShowPricing = setShowPricingProp || setLocalShowPricing;

  const [mainView, setMainView] = useState("today");

  // Deep-link from new-lead email: ?leadId=UUID opens the matching lead on
  // load. Re-checks every time requests changes because the email is sent
  // BEFORE the new row reaches the tradie's client, so the lead may not be
  // in state on the first render. We clear the URL param once applied so
  // refreshing the page doesn't keep yanking the user back.
  const [pendingDeepLinkLeadId, setPendingDeepLinkLeadId] = useState(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    return params.get("leadId") || null;
  });

  useEffect(() => {
    if (!pendingDeepLinkLeadId) return;
    const exists = requests.some((r) => r.id === pendingDeepLinkLeadId);
    if (!exists) return;
    setMainView("pipeline");
    setSelectedId(pendingDeepLinkLeadId);
    setMobileDetailOpen(true);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("leadId");
      window.history.replaceState({}, "", url.toString());
    } catch { /* ignore */ }
    setPendingDeepLinkLeadId(null);
  }, [pendingDeepLinkLeadId, requests]);
  const [overviewDismissed, setOverviewDismissed] = useState(() => {
    try {
      return localStorage.getItem(getOverviewDismissedStorageKey(businessId)) === "1";
    } catch {
      return false;
    }
  });
  const [pipelineViewMode, setPipelineViewMode] = useState(() => {
    try {
      return normalizePipelineViewMode(localStorage.getItem(getPipelineViewStorageKey(businessId)));
    } catch {
      return "board";
    }
  });
  const [requestedMarketplaceTab, setRequestedMarketplaceTab] = useState(null);
  const handleMarketplaceTabApplied = useCallback(() => setRequestedMarketplaceTab(null), []);
  // When a tab shortcut is clicked on a lead card, the desired tab is stored
  // here before selectedId changes. LeadDetailPanel reads + clears it in its
  // selected?.id effect, skipping the normal "reset to quote" behaviour.
  const detailTabOverrideRef = useRef(null);

  function openLeadDetailTab(id, tab = "quote") {
    detailTabOverrideRef.current = tab;
    handleSelectLead(id);
    setMainView("pipeline");
  }

  const { showToast } = useToast();
  // God-mode users (Shannon + Andrew) get admin tools and early workspace views.
  // Server-side RPC remains the source of truth for privileged DB work.
  const { isGodMode } = useGodMode();

  // ── In-app toast on newly-accepted quotes ────────────────────────────────
  // Polls the requests array for any quote whose customer_status is now
  // 'accepted' AND whose customer_responded_at timestamp is newer than the
  // last value seen on this device. Fires one toast per new accept and
  // updates the local seen-at marker. Best-effort — no dependency on
  // realtime subscriptions, no cross-device sync; that's a follow-up.
  const STORAGE_KEY_LAST_ACCEPT_SEEN = "rqp_last_accept_seen_at";
  useEffect(() => {
    if (!Array.isArray(requests) || requests.length === 0) return;
    let lastSeen = 0;
    try {
      const v = localStorage.getItem(STORAGE_KEY_LAST_ACCEPT_SEEN);
      lastSeen = v ? Number(v) : 0;
    } catch {
      /* storage blocked */
    }
    const accepts = requests
      .filter(
        (r) =>
          r?.quote?.customer_status === "accepted" &&
          r?.quote?.customer_responded_at
      )
      .map((r) => ({
        id: r.id,
        customerName:
          r?.form?.fullName || r?.customer || "A customer",
        at: new Date(r.quote.customer_responded_at).getTime(),
      }))
      .filter((a) => a.at > lastSeen);

    if (accepts.length === 0) return;

    accepts.forEach((a) => {
      showToast(`🎉 ${a.customerName} accepted your quote`, "success");
    });

    const newest = Math.max(...accepts.map((a) => a.at));
    try {
      localStorage.setItem(STORAGE_KEY_LAST_ACCEPT_SEEN, String(newest));
    } catch {
      /* storage blocked */
    }
    // Intentionally not depending on showToast (it's stable from the toast
    // provider). requests is the only meaningful trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests]);
  const { markAsRead, isRead, markMediaSeen, isMediaSeen } = useReadMessages();

  const selected =
    requests.find((r) => r.id === selectedId) || requests[0] || null;

  const selectedIsUnlocked = isLeadUnlocked(selected, businessId, unlockedLeadIds);
  const showSetupOverview = shouldShowSetupOverview({
    profile,
    requests,
    overviewDismissed,
  });
  const useMiniCRM = shouldShowMiniCRM(profile) || isGodMode;

  useEffect(() => {
    if (showSetupOverview && mainView === "today") {
      setMainView("overview");
    }
  }, [showSetupOverview, mainView]);

  useEffect(() => {
    try {
      setPipelineViewMode(
        normalizePipelineViewMode(localStorage.getItem(getPipelineViewStorageKey(businessId)))
      );
    } catch {
      setPipelineViewMode("board");
    }
  }, [businessId]);

  function dismissOverviewAndGo(view) {
    setOverviewDismissed(true);
    try {
      localStorage.setItem(getOverviewDismissedStorageKey(businessId), "1");
    } catch {
      /* storage blocked */
    }
    setMainView(view);
  }

  function setSavedPipelineViewMode(mode) {
    const next = normalizePipelineViewMode(mode);
    setPipelineViewMode(next);
    try {
      localStorage.setItem(getPipelineViewStorageKey(businessId), next);
    } catch {
      /* storage blocked */
    }
  }

  const isFree = subscription?.isFree;
  const isEnterprise = subscription?.tier?.id === "enterprise";
  const workspaceTabs = [
    ...(showSetupOverview ? [{ key: "overview", label: "Overview" }] : []),
    ...(showSetupOverview ? [{ key: "today", label: "Home" }] : []),
  ];
  const upgradeButtonStyle = isFree
    ? {
        flexShrink: 0,
        background: "var(--theme-accent, #FF8A00)",
        border: "1px solid var(--theme-accent, #FF8A00)",
        borderRadius: "999px",
        padding: "8px 18px",
        fontSize: "0.85rem",
        fontWeight: 700,
        color: "#fff",
        cursor: "pointer",
        whiteSpace: "nowrap",
        boxShadow: "0 2px 8px rgba(255, 138, 0,0.25)",
      }
    : {
        flexShrink: 0,
        background: "none",
        border: "1px solid var(--border, #e5e7eb)",
        borderRadius: "999px",
        padding: "6px 14px",
        fontSize: "0.8rem",
        fontWeight: 600,
        color: "var(--muted, #6b7280)",
        cursor: "pointer",
        whiteSpace: "nowrap",
        opacity: isEnterprise ? 0.5 : 1,
      };

  return (
    <>
      <div>
        {/* ── Header row: business name + Manage plan pill ── */}
        {mainView !== "today" && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
            <BusinessHeader profile={profile} />
            <button
              type="button"
              onClick={() => setShowPricing(true)}
              style={upgradeButtonStyle}
            >
              {subscription?.isFree ? "Upgrade plan" : "Manage plan"}
            </button>
          </div>
        )}

        {/* ── View switcher ── */}
        {workspaceTabs.length > 1 && (
        <div
          style={{
            display: "flex",
            width: "100%",
            background: "var(--card-bg, #fff)",
            border: "1px solid var(--border, #e5e7eb)",
            borderRadius: "16px",
            padding: "5px",
            boxShadow: "var(--shadow-sm, 0 1px 4px rgba(0,0,0,0.06))",
            marginTop: "16px",
            marginBottom: "20px",
          }}
        >
          {workspaceTabs.map(({ key, label }) => {
            const isActive = mainView === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setMainView(key)}
                style={{
                  flex: 1,
                  padding: "12px 0",
                  borderRadius: "12px",
                  border: "none",
                  background: isActive ? "var(--theme-primary-gradient, var(--theme-accent, #FF8A00))" : "transparent",
                  color: isActive ? "#fff" : "var(--text-soft, var(--text-muted, #6b7280))",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  cursor: "pointer",
                  transition: "all 0.18s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "7px",
                  boxShadow: isActive ? "0 2px 8px var(--theme-accent-glow, rgba(255, 138, 0,0.25))" : "none",
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "rgba(0,0,0,0.04)"; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
              >
                {label}
              </button>
            );
          })}
        </div>
        )}

        {/* ── Today view ── */}
        {mainView === "overview" && (
          <TradieOverview
            profile={profile}
            businessId={businessId}
            requests={requests}
            onGoToToday={() => dismissOverviewAndGo("today")}
            onGoToPipeline={() => dismissOverviewAndGo("pipeline")}
            onGoToSettings={onGoToSettings}
          />
        )}

        {mainView === "today" && (
          <TodayScreen
            profile={profile}
            requests={requests}
            unlockedLeadIds={unlockedLeadIds}
            businessId={businessId}
            createOnsiteQuoteLead={createOnsiteQuoteLead}
            onSwitchToPipeline={() => setMainView("pipeline")}
            onGoToLead={(id) => openLeadDetailTab(id, "quote")}
            onGoToLeadQuote={(id) => openLeadDetailTab(id, "quote")}
            onGoToLeadMessages={(id) => openLeadDetailTab(id, "messages")}
            onGoToLeadInvoice={(id) => openLeadDetailTab(id, "invoice")}
            onGoToLeadMedia={(id) => openLeadDetailTab(id, "overview")}
            onSwitchToNewJobs={() => {
              setMainView("pipeline");
              setRequestedMarketplaceTab("get_new_jobs");
            }}
            getCustomerQuoteLink={getCustomerQuoteLink}
            getCustomerInvoiceLink={getCustomerInvoiceLink}
            isRead={isRead}
            markAsRead={markAsRead}
          />
        )}

        {/* ── Pipeline view ── */}
        {mainView === "pipeline" && (
          <>
            <div className="rq-board-topbar">
              <button
                type="button"
                className="rq-board-home-button"
                onClick={() => setMainView("today")}
              >
                Home
              </button>
              <div>
                <h1>Job board</h1>
                <p>Move jobs, open details, quote, message and invoice.</p>
              </div>
            </div>
            <SubscriptionBanner subscription={subscription} onShowPricing={() => setShowPricing(true)} />

            {leadsLoading ? (
              <Card>
                <h2>Quote pipeline</h2>
                <p className="muted">Loading your leads…</p>
              </Card>
            ) : !requests.length ? (
              <Card>
                <h2>Quote pipeline</h2>
                <EmptyState
                  icon="✨"
                  title="No quote requests yet"
                  description="Share your direct intake link with customers, or invite them to submit a request. New leads land here ready to quote."
                />
              </Card>
            ) : (
              <>
                {useMiniCRM && (
                  <div className="rq-pipeline-view-toggle" role="tablist" aria-label="Job board view">
                    {[
                      { key: "board", label: "Board" },
                      { key: "list", label: "List" },
                    ].map((view) => {
                      const active = pipelineViewMode === view.key;
                      return (
                        <button
                          key={view.key}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          className={active ? "is-active" : ""}
                          onClick={() => setSavedPipelineViewMode(view.key)}
                        >
                          {view.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                {useMiniCRM && pipelineViewMode === "board" ? (
                  <>
                <Pipeline
                  requests={requests}
                  updateLeadStage={updateLeadStage}
                  archiveLead={archiveLead}
                  unarchiveLead={unarchiveLead}
                  updateLeadContactDetails={updateLeadContactDetails}
                  onOpenDetail={handleSelectLead}
                />
                {/* Mini-CRM detail overlay — kanban takes full width so the
                    detail panel slides up over the board on every viewport
                    (not just mobile). Triggered by handleSelectLead in
                    PipelineCard's "Open detail" button. */}
                {mobileDetailOpen && selected && (
                  <div
                    className="rqp-detail-overlay"
                    style={{
                      position: "fixed",
                      inset: 0,
                      zIndex: 1000,
                      background: "rgba(0,0,0,0.5)",
                      display: "flex",
                      alignItems: "flex-end",
                    }}
                    onClick={() => setMobileDetailOpen(false)}
                  >
                    <div
                      className="rqp-detail-sheet"
                      style={{
                        position: "relative",
                        width: "100%",
                        maxHeight: "92vh",
                        overflowY: "auto",
                        background: "var(--bg, #f9fafb)",
                        borderRadius: "20px 20px 0 0",
                        boxShadow: "0 -8px 40px rgba(0,0,0,0.3)",
                        padding: "8px 0 0",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="rqp-detail-sheet__handle-wrap">
                        <div className="rqp-detail-sheet__handle" />
                      </div>
                      {/* Close button */}
                      <button
                        type="button"
                        onClick={() => setMobileDetailOpen(false)}
                        aria-label="Close lead details"
                        style={{
                          position: "absolute",
                          top: "12px",
                          right: "16px",
                          background: "var(--surface, #fff)",
                          border: "1px solid var(--border, #e5e7eb)",
                          borderRadius: "50%",
                          width: "32px",
                          height: "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          fontSize: "1.1rem",
                          color: "var(--text-muted, #6b7280)",
                          zIndex: 1,
                        }}
                      >
                        ×
                      </button>
                      <div className="rqp-detail-sheet__body">
                        <LeadDetailPanel
                          selected={selected}
                          selectedIsUnlocked={selectedIsUnlocked}
                          profile={profile}
                          businessId={businessId}
                          leadsLoading={leadsLoading}
                          subscription={subscription}
                          unlockLead={unlockLead}
                          updateRequestStatus={updateRequestStatus}
                          saveQuote={saveQuote}
                          getCustomerQuoteLink={getCustomerQuoteLink}
                          getCustomerInvoiceLink={getCustomerInvoiceLink}
                          loadLeadDetails={loadLeadDetails}
                          onOpenBookModal={(lead) => setBookJobLead(lead)}
                          onUpdateContactDetails={updateLeadContactDetails}
                          detailTabOverrideRef={detailTabOverrideRef}
                          markAsRead={markAsRead}
                          isRead={isRead}
                          markMediaSeen={markMediaSeen}
                          isMediaSeen={isMediaSeen}
                          setShowPricing={setShowPricing}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="two-grid">
                <LeadMarketplace
                  requests={requests}
                  selected={selected}
                  onSelectLead={handleSelectLead}
                  unlockedLeadIds={unlockedLeadIds}
                  unlockLead={unlockLead}
                  creditBalance={creditBalance}
                  businessId={businessId}
                  dismissLead={dismissLead}
                  undismissLead={undismissLead}
                  dismissedLeadIds={dismissedLeadIds}
                  onSetDetailTab={(tab) => { detailTabOverrideRef.current = tab; }}
                  onOpenBookModal={(lead) => setBookJobLead(lead)}
                  onUpdateContactDetails={updateLeadContactDetails}
                  profileTradeTypes={profile?.trade_types || []}
                  profile={profile}
                  onGetNewJobsBlocked={() => setShowPricing(true)}
                  canAccessLeads={subscription?.canAccessLeads ?? true}
                  isJobLimitReached={subscription?.isJobLimitReached ?? false}
                  requestedTab={requestedMarketplaceTab}
                  onTabApplied={handleMarketplaceTabApplied}
                  isRead={isRead}
                  onGoToSettings={onGoToSettings}
                />

                <>
                  {/* Desktop: normal grid position */}
                  <div className="desktop-only-detail">
                    <LeadDetailPanel
                      selected={selected}
                      selectedIsUnlocked={selectedIsUnlocked}
                      profile={profile}
                      businessId={businessId}
                      leadsLoading={leadsLoading}
                      subscription={subscription}
                      unlockLead={unlockLead}
                      updateRequestStatus={updateRequestStatus}
                      saveQuote={saveQuote}
                      getCustomerQuoteLink={getCustomerQuoteLink}
                      getCustomerInvoiceLink={getCustomerInvoiceLink}
                      loadLeadDetails={loadLeadDetails}
                      onOpenBookModal={(lead) => setBookJobLead(lead)}
                      onUpdateContactDetails={updateLeadContactDetails}
                      detailTabOverrideRef={detailTabOverrideRef}
                      markAsRead={markAsRead}
                      isRead={isRead}
                      markMediaSeen={markMediaSeen}
                      isMediaSeen={isMediaSeen}
                      setShowPricing={setShowPricing}
                    />
                  </div>

                  {/* Mobile: slide-up overlay */}
                  {mobileDetailOpen && selected && (
                    <div
                      style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 1000,
                        background: "rgba(0,0,0,0.5)",
                        display: "none",
                      }}
                      className="mobile-detail-overlay"
                      onClick={() => setMobileDetailOpen(false)}
                    >
                      <div
                        style={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          maxHeight: "92vh",
                          overflowY: "auto",
                          background: "var(--bg, #f9fafb)",
                          borderRadius: "20px 20px 0 0",
                          boxShadow: "0 -8px 40px rgba(0,0,0,0.3)",
                          padding: "8px 0 0",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Drag handle */}
                        <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 12px" }}>
                          <div style={{ width: "36px", height: "4px", borderRadius: "2px", background: "var(--border, #d1d5db)" }} />
                        </div>
                        {/* Close button */}
                        <button
                          type="button"
                          onClick={() => setMobileDetailOpen(false)}
                          aria-label="Close lead details"
                          style={{
                            position: "absolute",
                            top: "12px",
                            right: "16px",
                            background: "var(--surface, #fff)",
                            border: "1px solid var(--border, #e5e7eb)",
                            borderRadius: "50%",
                            width: "32px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            fontSize: "1.1rem",
                            color: "var(--text-muted, #6b7280)",
                            zIndex: 1,
                          }}
                        >
                          ×
                        </button>
                        <div style={{ padding: "0 12px 24px" }}>
                          <LeadDetailPanel
                            selected={selected}
                            selectedIsUnlocked={selectedIsUnlocked}
                            profile={profile}
                            businessId={businessId}
                            leadsLoading={leadsLoading}
                            subscription={subscription}
                            unlockLead={unlockLead}
                            updateRequestStatus={updateRequestStatus}
                            saveQuote={saveQuote}
                            getCustomerQuoteLink={getCustomerQuoteLink}
                            getCustomerInvoiceLink={getCustomerInvoiceLink}
                            loadLeadDetails={loadLeadDetails}
                            onOpenBookModal={(lead) => setBookJobLead(lead)}
                            onUpdateContactDetails={updateLeadContactDetails}
                            detailTabOverrideRef={detailTabOverrideRef}
                            markAsRead={markAsRead}
                            isRead={isRead}
                            markMediaSeen={markMediaSeen}
                            isMediaSeen={isMediaSeen}
                            setShowPricing={setShowPricing}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              </div>
            )}
          </>
            )}
          </>
        )}
      </div>

      {bookJobLead && (
        <BookJobModal
          lead={bookJobLead}
          onConfirm={async (dateISO) => {
            await bookJob(bookJobLead.id, dateISO);
            setBookJobLead(null);
          }}
          onClose={() => setBookJobLead(null)}
        />
      )}

      {showPricing && (
        <SubscriptionPage
          subscription={subscription}
          onClose={() => setShowPricing(false)}
          onUpgrade={(tierId) => {
            showToast("Stripe payments coming soon — you'll be first to know!", "info");
            setShowPricing(false);
          }}
        />
      )}
    </>
  );
}
